/**
 * Manage List visibility — mirrors Offers pipeline roles (Admin / Director / Sales).
 */

export type ArtListVisibilityRole = "admin" | "director" | "sales";

export function mapProfileRoleToArtListRole(
  profileRole: string | undefined,
): ArtListVisibilityRole {
  const r = (profileRole || "").trim().toLowerCase();
  if (r === "admin") return "admin";
  if (r === "director") return "director";
  if (r === "sales") return "sales";
  return "sales";
}

/** Odoo domain OR-clauses for `art.list` search (Manage List fetch). */
export function buildArtListVisibilityOrParts(params: {
  visibilityRole: ArtListVisibilityRole;
  forUserId?: number;
  forCompanyIds?: number[];
}): unknown[][] {
  const { visibilityRole, forUserId, forCompanyIds = [] } = params;
  const hasUser = forUserId != null && Number.isFinite(forUserId);
  const hasCompany = forCompanyIds.length > 0;
  const orParts: unknown[][] = [];

  if (visibilityRole === "admin") {
    return orParts;
  }
  if (visibilityRole === "director") {
    if (hasUser) {
      orParts.push(["create_uid", "=", forUserId]);
      orParts.push(["share_ids.user_id", "=", forUserId]);
    }
    if (hasCompany) {
      orParts.push(["company_share_ids.company_id", "in", forCompanyIds]);
      orParts.push(["create_uid.company_ids", "in", forCompanyIds]);
    }
    orParts.push(["visibility", "=", "audience"]);
    return orParts;
  }
  if (hasUser) {
    orParts.push(["create_uid", "=", forUserId]);
    orParts.push(["share_ids.user_id", "=", forUserId]);
  }
  return orParts;
}

export type ArtListSection = "my" | "shared" | "team";

/**
 * Bucket a list into MY / SHARED WITH ME / GALLERY TEAM sections.
 *
 * Admin & Director: other people's lists (not team-shared) → "shared".
 * Sales: only own + explicitly shared (domain already filters fetch).
 */
export function classifyArtListSection(params: {
  visibilityRole: ArtListVisibilityRole;
  isOwner: boolean;
  hasActiveUserShare: boolean;
  companyPermission: "write" | "read" | null;
  visibility?: string;
}): ArtListSection {
  const { visibilityRole, isOwner, hasActiveUserShare, companyPermission, visibility } =
    params;

  if (isOwner) return "my";

  const isGalleryTeamBucket =
    companyPermission !== null ||
    visibility === "audience" ||
    visibility === "team";

  if (isGalleryTeamBucket) return "team";

  if (hasActiveUserShare) return "shared";

  if (visibilityRole === "admin" || visibilityRole === "director") {
    return "shared";
  }

  return "shared";
}

/**
 * Edit vs view-only on a list card / detail.
 * - Admin: write on every list they can see.
 * - Director: write on own, gallery team, gallery peers, audience; read if explicitly shared read-only.
 * - Sales: write only when owner or granted write (share / company).
 */
export function resolveArtListPermission(params: {
  visibilityRole: ArtListVisibilityRole;
  isOwner: boolean;
  sharePermission?: string;
  shareState?: string;
  companyPermission: "write" | "read" | null;
  visibility?: string;
}): "write" | "read" {
  const {
    visibilityRole,
    isOwner,
    sharePermission,
    shareState,
    companyPermission,
    visibility,
  } = params;

  if (visibilityRole === "admin") return "write";
  if (isOwner) return "write";

  const activeUserShare = shareState === "active";
  if (activeUserShare && sharePermission === "read") return "read";

  if (visibilityRole === "director") {
    if (activeUserShare && sharePermission === "write") return "write";
    if (companyPermission !== null) return "write";
    if (visibility === "audience" || visibility === "team") return "write";
    return "write";
  }

  if (activeUserShare && sharePermission === "write") return "write";
  if (companyPermission === "write") return "write";
  return "read";
}

/** Normalize Odoo `company_ids` / m2m values to numeric company ids. */
export function normalizeOdooCompanyIds(companyIds: unknown): number[] {
  if (!Array.isArray(companyIds)) return [];
  return companyIds
    .map((entry) => {
      if (typeof entry === "number" && Number.isFinite(entry)) return entry;
      if (Array.isArray(entry) && typeof entry[0] === "number") return entry[0];
      const n = Number(entry);
      return Number.isFinite(n) ? n : null;
    })
    .filter((id): id is number => id != null && id > 0);
}

/** Creator belongs to at least one of the viewer's companies (gallery peer). */
export function sharesCompanyWithCreator(
  creatorCompanyIds: number[],
  viewerCompanyIds: number[],
): boolean {
  if (creatorCompanyIds.length === 0 || viewerCompanyIds.length === 0) return false;
  const viewer = new Set(viewerCompanyIds);
  return creatorCompanyIds.some((id) => viewer.has(id));
}

/**
 * Whether the Manage List UI should offer delete (row trash + bulk select).
 * - Admin: any list with write (all visible lists).
 * - Director: own lists + same-company creator (gallery peer) lists with write.
 * - Sales: own lists with write only.
 */
export function resolveArtListCanDelete(params: {
  visibilityRole: ArtListVisibilityRole;
  permission: "write" | "read";
  isOwner: boolean;
  isGalleryPeer: boolean;
}): boolean {
  const { visibilityRole, permission, isOwner, isGalleryPeer } = params;
  if (permission !== "write") return false;
  if (visibilityRole === "admin") return true;
  if (visibilityRole === "director") return isOwner || isGalleryPeer;
  return isOwner;
}

/** Director: list creator is outside the viewer's gallery / company. */
export function resolveArtListIsOtherGallery(params: {
  visibilityRole: ArtListVisibilityRole;
  isOwner: boolean;
  isGalleryPeer: boolean;
}): boolean {
  const { visibilityRole, isOwner, isGalleryPeer } = params;
  return visibilityRole === "director" && !isOwner && !isGalleryPeer;
}

export function artListSharedState(params: {
  isOwner: boolean;
  visibility?: string;
  shareState?: string;
  hasCompanyAccess: boolean;
  visibilityRole: ArtListVisibilityRole;
}): "active" | "expired" {
  const { visibility, shareState, hasCompanyAccess, visibilityRole, isOwner } =
    params;
  if (isOwner) return "active";
  if (visibility === "audience") return "active";
  if (hasCompanyAccess) return "active";
  if (shareState === "active") return "active";
  if (visibilityRole === "admin" || visibilityRole === "director") {
    return "active";
  }
  return "expired";
}
